export enum Summary {
  Match = "match",
  PartialMatch = "partial_match",
  NoMatch = "no_match",
  NoData = "no_data",
  NoInput = "no_input"
}

interface CheckSummary {
  summary: Summary;
}

enum YesNoData {
  Yes = "yes",
  No = "no",
  NoData = "no_data"
}

enum AddressType {
  Residential = "residential",
  Commercial = "commercial",
  NoData = "no_data"
}

export enum KycCheckStatus {
  Success = "success",
  NotApplicable = "not_applicable",
  ManuallyApproved = "manually_approved"
}

export type PlaidReport = {
  status: string;
  lightning_status: string;
  risk_check_status: string;
  session_id: string;
  dob: string;
  ssn: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  last_name: string;
  first_name: string;
  middle_name: string;
  phone: string;
  name_summary: Summary;
  dob_summary: Summary;
  phone_summary: Summary;
  address_summary: Summary;
  ssn_summary: Summary;
  po_box: Summary;
  email_risk_score: number;
  device_risk_score: number;
  identity_verification_network_risk_score: number;
  phone_risk_score: number;
  stolen_identity_risk_score: number;
  synthetic_identity_risk_score: number;
  behaviour_risk_score: number;
  id_proof_front_side_doc: string;
  id_proof_other_side_doc: string;

  ip_address: string;

  // Not used
  risk_check?: unknown;

  kyc_check?: {
    name: CheckSummary;
    status: string | "success" | "manually_approved";
    address: CheckSummary & {
      type: AddressType;
      po_box: YesNoData;
    };
    id_number: CheckSummary;
    phone_number: CheckSummary & {
      area_code: Summary;
    };
    date_of_birth: CheckSummary;
  };
  kyc_check_status?: KycCheckStatus;
  selfie_check_status?: string | "success";
  selfie_check?: {
    selfies: (SelfieCheckSelfie & {
      assets?: {
        image_url?: Asset | null;
        video_url?: Asset | null;
        liveness?: Asset | null;
      };
    })[];
  };
  documents?: DocumentaryVerificationDocument[];
};

export enum DocumentStatus {
  Success = "success",
  Failed = "failed",
  ManuallyApproved = "manually_approved"
}

enum PhysicalDocumentCategory {
  DriversLicense = "drivers_license",
  IdCard = "id_card",
  Passport = "passport",
  ResidencePermitCard = "residence_permit_card",
  ResidentCard = "resident_card",
  Visa = "visa"
}
export enum ImageQuality {
  High = "high",
  Medium = "medium",
  Low = "low"
}

export enum DocumentMatchCode {
  Match = "match",
  PartialMatch = "partial_match",
  NoMatch = "no_match",
  NoData = "no_data"
}

export type Asset = {
  asset?: {
    url: string;
    content_type: string;
    filename: string;
    size: number;
    extension: string;
  };
};

interface DocumentaryVerificationDocument {
  status: DocumentStatus;
  attempt: number;
  assets: {
    original_front: Asset | null;
    original_back: Asset | null;
    cropped_front: Asset | null;
    cropped_back: Asset | null;
    face: Asset | null;
  } | null;

  extracted_data: {
    id_number: string | null;
    category: PhysicalDocumentCategory;
    expiration_date: string | null;
    issuing_country: string;
    issuing_region: string | null;
    date_of_birth: string | null;
    address: {
      street: string;
      city: string;
      region: string | null;
      postal_code: string | null;
      country: string;
    } | null;
  } | null;
  /**
   *
   * @type {DocumentAnalysis}
   * @memberof DocumentaryVerificationDocument
   */
  analysis: {
    authenticity: DocumentAuthenticityMatchCode;
    image_quality: ImageQuality;
    extracted_data: PhysicalDocumentExtractedDataAnalysis | null;
  };

  redacted_at: string | null;
}

export enum ExpirationDate {
  NotExpired = "not_expired",
  Expired = "expired",
  NoData = "no_data"
}

enum IssuingCountry {
  Match = "match",
  NoMatch = "no_match"
}

interface PhysicalDocumentExtractedDataAnalysis {
  name: DocumentMatchCode;

  date_of_birth: DocumentMatchCode;

  expiration_date: ExpirationDate;
  /**
   *
   * @type {IssuingCountry}
   * @memberof PhysicalDocumentExtractedDataAnalysis
   */
  issuing_country: IssuingCountry;
}

enum DocumentAuthenticityMatchCode {
  Match = "match",
  PartialMatch = "partial_match",
  NoMatch = "no_match",
  NoData = "no_data"
}

enum SelfieCheckStatus {
  Success = "success",
  Failed = "failed"
}

interface SelfieCheckSelfie {
  status: SelfieCheckStatus;
  /**
   * The `attempt` field begins with 1 and increments with each subsequent selfie upload.
   */
  attempt: number;
  capture: {
    /**
     * Temporary URL for downloading an image selfie capture.
     */
    image_url: string | null;
    /**
     * Temporary URL for downloading a video selfie capture.
     */
    video_url: string | null;
  };
  analysis: {
    document_comparison: SelfieAnalysisDocumentComparison;
  };
}

enum SelfieAnalysisDocumentComparison {
  Match = "match",
  NoMatch = "no_match",
  NoInput = "no_input"
}
